import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useAxiosInstance } from "../../../services/Interceptor";
import {
  CreateAccolade,
  CreateOffer,
  getAthleteProfile,
  getOfferAcceptanceStatus,
  updateProfileIsPublic,
  updateUserBio,
} from "../../../services/AthleteService";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchOffers,
  fetchAccolades,
  fetchProfilePhoto,
} from "../../../store/AthleteActions";
import { setHasUsedFreeTrial as setHasUsedFreeTrialInStore } from "../../../store/slices/userSlice";
import useAuthUser from "react-auth-kit/hooks/useAuthUser";
import { uploadProfilePhoto } from "../../../services/FileUploadService";
import { PersonalInfoAthlete } from "../account/PersonalInfoAthlete";
import DeleteAccolade from "./delete-accolade";
import DeleteOffer from "./delete-offer";
import UpdatePassword from "./UpdatePassword";
import moment from "moment/moment";
import PaymentUpdate from "../../../components/PaymentUpdate";
import { Navigate } from "react-router-dom";
import AccountTabs from "./AccountTabs";
import AccoladesTab from "./AccoladesTab";
import OffersTab from "./OffersTab";
import ProfileVideoUploadsTab from "./ProfileVideoUploadsTab";
import ProfileVideoLinksTab from "./ProfileVideoLinksTab";
import WeightRoomTab from "./WeightRoomTab";
import AccoladeModal from "./AccoladeModal";
import OfferModal from "./OfferModal";
import OfferAcceptanceModal from "./OfferAcceptanceModal";
import AccountInfoModal from "./AccountInfoModal";
import UpdateBioSection from "./UpdateBioSection";
import ProfileSettingsSection from "./ProfileSettingsSection";
import ReviewSection from "./ReviewSection";
import { SaveOfferAcceptanceStatus } from "../../../services/EventsService";

function AthleteData() {
  const [enabled, setEnabled] = useState();
  let [isOpen, setIsOpen] = useState(false);
  let [isOfferOpen, setIsOfferOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const dispatch = useDispatch();
  const apiInstance = useAxiosInstance();
  const date = moment();

  const {
    register: accoladeRegister,
    handleSubmit: handleAccoladeSubmit,
    reset: resetAccolade,
    resetField,
    watch,
    formState: { errors },
  } = useForm({
    defaultValues: {
      accoladeType: "ALL_STATE",
      accoladeYear: date.year(),
    },
  });

  const selectedType = watch("accoladeType");
  useEffect(() => {
    resetField("accoladeDescription");
  }, [resetField, selectedType]);

  const {
    register: offerRegister,
    handleSubmit: handleOfferSubmit,
    reset: resetOffer,
  } = useForm();

  const {
    register: acceptanceRegister,
    handleSubmit: handleAcceptanceSubmit,
    watch: watchAcceptance,
    setValue: setAcceptanceValue,
    formState: { errors: acceptanceErrors },
  } = useForm({
    defaultValues: { offerAcceptanceStatus: "", offerAcceptanceSchool: "" },
  });

  const offerAcceptanceStatus = watchAcceptance("offerAcceptanceStatus");

  const { offers, accolades, profilePhoto } = useSelector(
    (state) => state.athletes,
  );
  const hasSubscription = useSelector((state) => state.user.hasSubscription);
  const authUser = useAuthUser();
  const hasSubscriptionAccess =
    hasSubscription || authUser?.user?.role?.toLowerCase?.() === "highschool";
  const [isUploading, setIsUploading] = useState(false);
  const [profilePicture, setProfilePicture] = useState(null);
  const [deleteAccoladeOpen, setDeleteAccoladeOpen] = useState(false);
  const [accoladeIdSelected, setAccoladeIdSelected] = useState(undefined);
  const [deleteOfferOpen, setDeleteOfferOpen] = useState(false);
  const [offerIdSelected, setOfferIdSelected] = useState(undefined);
  const [bio, setBio] = useState("");
  const [isUpdatingBio, setIsUpdatingBio] = useState(false);
  const [hasUsedFreeTrial, setHasUsedFreeTrial] = useState(false);
  const [tab, setTab] = useState(1);
  const [showInfoUpload, setShowInfoUpload] = useState(false);
  const [showInfoLinks, setShowInfoLinks] = useState(false);
  const [isOfferAcceptanceOpen, setIsOfferAcceptanceOpen] = useState(false);
  const [hasAcceptedOffer, setHasAcceptedOffer] = useState("no");
  const [savedHasAccepted, setSavedHasAccepted] = useState("no");
  const [savedAcceptanceData, setSavedAcceptanceData] = useState({
    offerAcceptanceStatus: "",
    offerAcceptanceSchool: "",
  });

  useEffect(() => {
    if (!authUser || !authUser?.user) {
      return <Navigate to="/sign-in" replace />;
    }
  });

  useEffect(() => {
    if (!authUser?.user?.id) return;
    dispatch(fetchOffers(apiInstance, authUser.user.id));
    dispatch(fetchAccolades(apiInstance, authUser.user.id));
    dispatch(fetchProfilePhoto(apiInstance, authUser.user.id));
  }, [dispatch, apiInstance, authUser?.user?.id]);

  useEffect(() => {
    const loadAcceptanceStatus = async () => {
      try {
        const data = await getOfferAcceptanceStatus(
          apiInstance,
          authUser.user.id,
        );
        if (data) {
          const status = data.offerAcceptanceStatus || "";
          const school = data.offerAcceptanceSchool || "";
          const hasAccepted = ["Committed", "Signed"].includes(status)
            ? "yes"
            : "no";
          setHasAcceptedOffer(hasAccepted);
          setSavedHasAccepted(hasAccepted);
          setSavedAcceptanceData({
            offerAcceptanceStatus: status,
            offerAcceptanceSchool: school,
          });
          setAcceptanceValue("offerAcceptanceStatus", status);
          setAcceptanceValue("offerAcceptanceSchool", school);
        }
      } catch {
        // silent — no saved status yet
      }
    };
    if (authUser?.user?.id) loadAcceptanceStatus();
  }, [apiInstance, authUser.user.id, setAcceptanceValue]);

  useEffect(() => {
    if (profilePhoto) {
      setProfilePicture(profilePhoto);
    }
    const getProfile = async () => {
      try {
        const response = await getAthleteProfile(
          apiInstance,
          authUser?.user?.id,
        );
        if (response) {
          setEnabled(response.isPublic);
          setBio(response.bio);
          setHasUsedFreeTrial(!!response.hasUsedFreeTrial);
          dispatch(setHasUsedFreeTrialInStore(!!response.hasUsedFreeTrial));
        }
      } catch (error) {
        toast.error(
          error.response?.data?.message || "Failed to load profile data",
          { position: "top-right" },
        );
      }
    };

    if (authUser?.user?.id) {
      getProfile();
    }
  }, [profilePhoto, authUser, apiInstance, dispatch]);

  const handleAccoladeSubmission = async (formData) => {
    if (!authUser?.user?.id) return;
    setLoading(true);

    const payload = {
      accoladeDescription: formData.accoladeDescription,
      accoladeClassification: formData.accoladeClassification,
      accoladeYear: Number(formData.accoladeYear),
      accoladeLink: formData.accoladeType || null,
    };

    try {
      await CreateAccolade(apiInstance, payload);
      toast.success("Accolade created successfully!", {
        position: "top-right",
      });
      setIsOpen(false);
      resetAccolade();
      dispatch(fetchAccolades(apiInstance, authUser?.user?.id));
    } catch (error) {
      toast.error("Failed to create Accolade!", {
        position: "top-right",
      });
    } finally {
      setLoading(false);
    }
  };

  // Offer form submission
  const handleOfferSubmission = async (data) => {
    if (!authUser?.user?.id) return;
    setLoading(true);
    try {
      await CreateOffer(apiInstance, data);
      toast.success("Offer created successfully!", { position: "top-right" });
      setIsOfferOpen(false);
      resetOffer();
      dispatch(fetchOffers(apiInstance, authUser?.user?.id));
    } catch (error) {
      toast.error("Failed to create Offer!", { position: "top-right" });
    } finally {
      setLoading(false);
    }
  };

  const validateFile = (file) => {
    const permittedExtensions = [".jpg", ".jpeg", ".png", ".gif"];
    const permittedMimeTypes = ["image/jpeg", "image/png", "image/gif"];
    const maxFileSize = 10 * 1024 * 1024;

    const fileExtension = file.name
      .substring(file.name.lastIndexOf("."))
      .toLowerCase();
    const isExtensionValid = permittedExtensions.includes(fileExtension);
    const isMimeTypeValid = permittedMimeTypes.includes(file.type);
    const isSizeValid = file.size <= maxFileSize;

    if (!isExtensionValid) {
      toast.error(
        "Invalid file extension. Only JPG, PNG, and GIF are allowed.",
        {
          position: "top-right",
        },
      );
      return false;
    }

    if (!isMimeTypeValid) {
      toast.error("Invalid file type. Please upload an image file.", {
        position: "top-right",
      });
      return false;
    }

    if (!isSizeValid) {
      toast.error("File size exceeds the 10 MB limit.", {
        position: "top-right",
      });
      return false;
    }

    return true;
  };

  const style = {
    ul: "flex space-x-0 mt-0 border-b border-gray-300",
    li: "text-gray-900 pb-2 px-3 hover:opacity-70 text-sm xl:text-base cursor-pointer",
    selected:
      "cursor-default border-b-4 border-blue-700 text-sm xl:text-base text-blue-700 pb-2 px-3 font-medium ",
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];

    if (!authUser?.user?.id || !file || !validateFile(file)) {
      return;
    }

    const formData = new FormData();
    formData.append("file", file);

    try {
      setIsUploading(true);
      await uploadProfilePhoto(apiInstance, formData);
      setTimeout(() => {
        dispatch(fetchProfilePhoto(apiInstance, authUser.user.id));
        setProfilePicture(profilePhoto);
        setIsUploading(false);
      }, 2000);
      toast.success("Photo uploaded successfully!", { position: "top-right" });
    } catch (error) {
      toast.error("Failed to upload photo!", { position: "top-right" });
      setIsUploading(false);
    }
  };

  const handleDragDropUpload = async (event) => {
    event.preventDefault();
    const file = event.dataTransfer.files[0];

    if (!authUser?.user?.id || !file || !validateFile(file)) {
      return;
    }

    const formData = new FormData();
    formData.append("file", file);

    try {
      setIsUploading(true);
      await uploadProfilePhoto(apiInstance, formData);
      setTimeout(() => {
        dispatch(fetchProfilePhoto(apiInstance, authUser.user.id));
        setProfilePicture(profilePhoto);
        setIsUploading(false);
      }, 2000);
      toast.success("Photo uploaded successfully!", { position: "top-right" });
    } catch (error) {
      toast.error("Failed to upload photo!", { position: "top-right" });
      setIsUploading(false);
    }
  };

  const handleDragOver = (event) => {
    event.preventDefault();
  };

  const openDeleteAccolade = (accoladeIde) => {
    setAccoladeIdSelected(accoladeIde);
    setDeleteAccoladeOpen(true);
  };

  const openDeleteOffer = (offerId) => {
    setOfferIdSelected(offerId);
    setDeleteOfferOpen(true);
  };

  const handleAcceptanceChange = (value) => {
    setHasAcceptedOffer(value);
    if (value === "yes") {
      setIsOfferAcceptanceOpen(true);
    }
  };

  const handleRemoveAcceptance = async () => {
    setLoading(true);
    const emptyPayload = {
      offerAcceptanceStatus: "",
      offerAcceptanceSchool: "",
    };
    try {
      await SaveOfferAcceptanceStatus(
        apiInstance,
        authUser.user.id,
        emptyPayload,
      );
      setHasAcceptedOffer("no");
      setSavedHasAccepted("no");
      setSavedAcceptanceData(emptyPayload);
      setAcceptanceValue("offerAcceptanceStatus", "");
      setAcceptanceValue("offerAcceptanceSchool", "");
    } catch (err) {
      console.error("Remove acceptance error:", err);
      toast.error("Failed to remove offer acceptance status!", {
        position: "top-right",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleAcceptanceModalClose = () => {
    setHasAcceptedOffer(savedHasAccepted);
    setIsOfferAcceptanceOpen(false);
    setAcceptanceValue(
      "offerAcceptanceStatus",
      savedAcceptanceData.offerAcceptanceStatus,
    );
    setAcceptanceValue(
      "offerAcceptanceSchool",
      savedAcceptanceData.offerAcceptanceSchool,
    );
  };

  const handleAcceptanceSubmission = async (data) => {
    setLoading(true);
    const payload = {
      offerAcceptanceStatus: data.offerAcceptanceStatus,
      offerAcceptanceSchool:
        data.offerAcceptanceStatus === "" ? "" : data.offerAcceptanceSchool,
    };
    try {
      await SaveOfferAcceptanceStatus(apiInstance, authUser.user.id, payload);
      toast.success("Offer acceptance status saved!", {
        position: "top-right",
      });
      const newHasAccepted = ["Committed", "Signed"].includes(
        payload.offerAcceptanceStatus,
      )
        ? "yes"
        : "no";
      setHasAcceptedOffer(newHasAccepted);
      setSavedHasAccepted(newHasAccepted);
      setSavedAcceptanceData({
        offerAcceptanceStatus: payload.offerAcceptanceStatus,
        offerAcceptanceSchool: payload.offerAcceptanceSchool || "",
      });
      setIsOfferAcceptanceOpen(false);
    } catch (error) {
      toast.error("Failed to save offer acceptance status!", {
        position: "top-right",
      });
    } finally {
      setLoading(false);
    }
  };

  const enableProfileIsPublic = async (e) => {
    if (!authUser?.user?.id) return;
    try {
      var updatedResponse = await updateProfileIsPublic(
        apiInstance,
        !e,
        authUser?.user?.id,
      );
      if (updatedResponse) {
        setEnabled(!e);
      }
    } catch (err) {
      toast.error(
        `Error to enable athlete status ${err?.error || "Unknown error"}`,
        {
          position: "top-center",
        },
      );
    }
  };

  const handleUpdateBio = async (e) => {
    e.preventDefault();
    if (!authUser?.user?.id) return;

    if (!bio?.trim()) {
      toast.warning("Bio cannot be empty.", { position: "top-right" });
      return;
    }

    if (bio?.length > 500) {
      toast.warning("Bio cannot exceed 500 characters.", {
        position: "top-right",
      });
      return;
    }

    try {
      setIsUpdatingBio(true);
      await updateUserBio(apiInstance, authUser?.user?.id, bio);
      toast.success("Bio updated successfully!", { position: "top-right" });
    } catch (error) {
      toast.error(
        "Error updating bio: " + (error?.message || "Unknown error"),
        {
          position: "top-right",
        },
      );
    } finally {
      setIsUpdatingBio(false);
    }
  };

  const ALL_STATE_OPTIONS = [
    "All State 1st Team",
    "All State 2nd Team",
    "All State Honorable Mention",
  ];

  const ALL_CONFERENCE_OPTIONS = [
    "All Conference/Region 1st Team",
    "All Conference/Region 2nd Team",
    "All Conference/Region Honorable Mention",
  ];

  const CLASSIFICATIONS = ["1A", "2A", "3A", "4A", "5A", "6A"];

  return (
    <>
      <div className="flex-none md:flex xl:flex bg-gray-100 gap-3 h-full">
        <div className="w-full md:w-2/3 xl:w-2/3">
          <div className="grid grid-cols-1 gap-y-3 h-full">
            {/* info profile */}
            <PersonalInfoAthlete />
            {/* update password */}
            <UpdatePassword title="Update Password" />
            {/* end  update password */}
            <UpdateBioSection
              bio={bio}
              setBio={setBio}
              onSubmit={handleUpdateBio}
              isUpdatingBio={isUpdatingBio}
              hasSubscriptionAccess={hasSubscriptionAccess}
              hasUsedFreeTrial={hasUsedFreeTrial}
            />
            <ProfileSettingsSection
              isUploading={isUploading}
              profilePicture={profilePicture}
              onDragDropUpload={handleDragDropUpload}
              onDragOver={handleDragOver}
              onFileUpload={handleFileUpload}
              enabled={enabled}
              onTogglePublicProfile={enableProfileIsPublic}
              hasSubscriptionAccess={hasSubscriptionAccess}
              hasUsedFreeTrial={hasUsedFreeTrial}
            />
          </div>
        </div>
        <div className="w-full md:w-2/3 xl:w-2/3 mt-4 md:mt-0 xl:mt-0">
          <div className="grid grid-cols-1 gap-y-3 h-full">
            {/* Accolades and Offers Section tab */}
            <div className="bg-white shadow-lg p-4 rounded-lg border-gray-300">
              <div className="w-full">
                <h2 className="text-xl mb-2 uppercase">Offers</h2>
                <div className="top-0 bg-white z-20">
                  <AccountTabs tab={tab} setTab={setTab} style={style} />
                  {tab === 1 && (
                    <AccoladesTab
                      accolades={accolades}
                      onOpenAdd={() => setIsOpen(true)}
                      onDelete={openDeleteAccolade}
                      hasSubscriptionAccess={hasSubscriptionAccess}
                      hasUsedFreeTrial={hasUsedFreeTrial}
                    />
                  )}
                  {tab === 2 && (
                    <OffersTab
                      offers={offers}
                      onOpenAdd={() => setIsOfferOpen(true)}
                      onDelete={openDeleteOffer}
                      hasAcceptedOffer={hasAcceptedOffer}
                      onAcceptanceChange={handleAcceptanceChange}
                      onRemoveAcceptance={handleRemoveAcceptance}
                      savedAcceptanceData={savedAcceptanceData}
                      onEditAcceptance={() => setIsOfferAcceptanceOpen(true)}
                      hasSubscriptionAccess={hasSubscriptionAccess}
                      hasUsedFreeTrial={hasUsedFreeTrial}
                    />
                  )}
                  {tab === 3 && (
                    <ProfileVideoUploadsTab
                      onShowInfo={() => setShowInfoUpload(true)}
                    />
                  )}
                  {tab === 4 && <ProfileVideoLinksTab />}
                  {tab === 5 && <WeightRoomTab />}
                </div>
              </div>
            </div>
            {/* Review  */}
            <ReviewSection
              hasSubscriptionAccess={hasSubscriptionAccess}
              hasUsedFreeTrial={hasUsedFreeTrial}
            />
            {hasSubscription && authUser?.user?.id && (
              <div className="bg-white shadow-lg p-4 rounded-lg border-gray-300">
                <div className="w-full">
                  <PaymentUpdate
                    account={authUser.user.id}
                    email={authUser.user.email}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <AccoladeModal
        open={isOpen}
        onClose={() => setIsOpen(false)}
        onSubmit={handleAccoladeSubmit(handleAccoladeSubmission)}
        accoladeRegister={accoladeRegister}
        errors={errors}
        selectedType={selectedType}
        dateYear={date.year()}
        allStateOptions={ALL_STATE_OPTIONS}
        allConferenceOptions={ALL_CONFERENCE_OPTIONS}
        classifications={CLASSIFICATIONS}
        loading={loading}
        resetAccolade={resetAccolade}
      />
      <OfferModal
        open={isOfferOpen}
        onClose={() => setIsOfferOpen(false)}
        onSubmit={handleOfferSubmit(handleOfferSubmission)}
        offerRegister={offerRegister}
        resetOffer={resetOffer}
        dateYear={date.year()}
        loading={loading}
      />
      <DeleteAccolade
        deleteAccoladeOpen={deleteAccoladeOpen}
        closeDeleteAccolade={() => setDeleteAccoladeOpen(false)}
        accoladeId={accoladeIdSelected}
        athleteId={authUser?.user?.id}
        onDeleteSuccess={() => {
          toast.success("Accolade deleted successfully!", {
            position: "top-right",
          });
        }}
      />
      <DeleteOffer
        deleteOfferOpen={deleteOfferOpen}
        closeDeleteOffer={() => setDeleteOfferOpen(false)}
        offerId={offerIdSelected}
      />
      <OfferAcceptanceModal
        open={isOfferAcceptanceOpen}
        onClose={handleAcceptanceModalClose}
        onSubmit={handleAcceptanceSubmit(handleAcceptanceSubmission)}
        acceptanceRegister={acceptanceRegister}
        offerAcceptanceStatus={offerAcceptanceStatus}
        errors={acceptanceErrors}
        loading={loading}
      />
      {/* Modals for information */}
      {showInfoUpload && (
        <AccountInfoModal
          title="Profile Video uploads"
          description="These videos will be viewable by coaches directly in the platform.  If the following videos are hosted elsewhere use the profile video links tab to add links that the coaches can see."
          onClose={() => setShowInfoUpload(false)}
        />
      )}

      {showInfoLinks && (
        <AccountInfoModal
          title="Profile video links"
          description="Coaches will be able to follow these links to an external site where you have hosted your videos.  Please ensure that the links you post are to publicly hosted videos.  If you prefer to upload and host videos directly in this platform use the Profile Video Uploads tab."
          onClose={() => setShowInfoLinks(false)}
        />
      )}
    </>
  );
}

export default AthleteData;
